
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ContactPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContactPopup({ open, onOpenChange }: ContactPopupProps) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {
      name: "",
      email: "",
      message: ""
    };

    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    }

    if (!formData.email.trim()) {
      newErrors.email = "E-mail é obrigatório";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Formato de e-mail inválido";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Mensagem é obrigatória";
    }

    setErrors(newErrors);
    return !newErrors.name && !newErrors.email && !newErrors.message;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    console.log('🚀 Iniciando envio de mensagem de contato...');

    try {
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      console.log('👤 Sessão do usuário:', !!session, session?.user?.email);
      
      if (!session) {
        console.error('❌ Usuário não autenticado');
        toast({
          title: "Erro de autenticação",
          description: "Você precisa estar logado para enviar mensagens.",
          variant: "destructive"
        });
        return;
      }

      console.log('📝 Dados do formulário:', {
        name: formData.name.trim(),
        email: formData.email.trim(),
        messageLength: formData.message.trim().length
      });

      // Call the Edge Function with proper headers
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          message: formData.message.trim()
        }
      });

      console.log('📨 Resposta da função:', { data, error });

      if (error) {
        console.error('❌ Erro da função send-contact-email:', error);
        
        let errorMessage = 'Erro ao enviar mensagem';
        let errorDescription = 'Tente novamente em alguns instantes.';
        
        // Tratar diferentes tipos de erro
        if (error.message?.includes('RESEND_API_KEY')) {
          errorDescription = 'Serviço de email não configurado. Entre em contato com o suporte.';
        } else if (error.message?.includes('Unauthorized')) {
          errorDescription = 'Sessão expirada. Tente fazer login novamente.';
        } else if (error.message) {
          errorDescription = error.message;
        }
        
        toast({
          title: errorMessage,
          description: errorDescription,
          variant: "destructive"
        });
        return;
      }

      if (data?.error) {
        console.error('❌ Erro retornado pela função:', data.error);
        toast({
          title: "Erro ao enviar",
          description: data.details || data.error,
          variant: "destructive"
        });
        return;
      }
      
      if (data?.success) {
        console.log('✅ Mensagem enviada com sucesso!', data);
        toast({
          title: "Mensagem enviada!",
          description: "Obrigado pelo seu contato. Responderemos em breve!",
        });
        
        // Limpar formulário e erros
        setFormData({ name: "", email: "", message: "" });
        setErrors({ name: "", email: "", message: "" });
        onOpenChange(false);
      } else {
        console.warn('⚠️ Resposta inesperada:', data);
        toast({
          title: "Aviso",
          description: "Resposta inesperada do servidor. A mensagem pode ter sido enviada.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('💥 Erro inesperado no envio:', error);
      toast({
        title: "Erro inesperado",
        description: "Verifique sua conexão e tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
      console.log('🏁 Processo de envio finalizado');
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Fale Conosco</DialogTitle>
          <DialogDescription>
            Queremos ouvir você! Conte-nos sobre problemas, sugestões ou ideias.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Seu nome"
              disabled={isSubmitting}
              className={errors.name ? "border-red-500" : ""}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">E-mail *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="seu@email.com"
              disabled={isSubmitting}
              className={errors.email ? "border-red-500" : ""}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message">Mensagem *</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleInputChange("message", e.target.value)}
              placeholder="Conte-nos sobre problemas, sugestões ou ideias..."
              rows={4}
              disabled={isSubmitting}
              className={errors.message ? "border-red-500" : ""}
            />
            {errors.message && (
              <p className="text-sm text-red-500">{errors.message}</p>
            )}
          </div>
          
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
